import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileCode2, Plus, Save, Undo2, Trash2, Loader2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";

const NewFile = ({ onCreate }) => {
  const [name, setName] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) { onCreate(name.trim()); setName(""); } }} className="flex gap-1 p-2 border-t border-slate-200">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="new-page.html" data-testid="code-new-file-input"
        className="flex-1 min-w-0 h-7 px-2 rounded bg-slate-50 border border-slate-200 text-[11px] font-mono text-slate-600 outline-none focus:border-sky-400" />
      <button type="submit" data-testid="code-new-file-btn" className="w-7 h-7 rounded bg-slate-100 text-slate-600 hover:text-slate-900 flex items-center justify-center" aria-label="Add file"><Plus size={13} /></button>
    </form>
  );
};

export const CodePane = ({ projectId, version, readOnly, onSaved }) => {
  const original = useMemo(() => Object.fromEntries((version?.files || []).map((f) => [f.path, f.content])), [version]);
  const [drafts, setDrafts] = useState({});
  const [active, setActive] = useState("index.html");
  const [saving, setSaving] = useState(false);
  const all = { ...original, ...drafts };
  const paths = Object.keys(all).sort((a, b) => (a === "index.html" ? -1 : b === "index.html" ? 1 : a.localeCompare(b)));
  const dirty = Object.keys(drafts).filter((p) => drafts[p] !== original[p]);

  useEffect(() => { setDrafts({}); if (version && !original[active]) setActive(paths[0] || "index.html"); }, [version?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.saveFiles(projectId, { files: dirty.map((p) => ({ path: p, content: drafts[p] })) });
      toast.success("Saved as a new version");
      setDrafts({});
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Could not save")); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!original[p]) { const d = { ...drafts }; delete d[p]; setDrafts(d); return; }
    try { await api.saveFiles(projectId, { deleted: [p] }); toast.success(`Deleted ${p}`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };

  const onKey = (e) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    const t = e.target, s = t.selectionStart;
    const v = t.value.slice(0, s) + "  " + t.value.slice(t.selectionEnd);
    setDrafts({ ...drafts, [active]: v });
    requestAnimationFrame(() => { t.selectionStart = t.selectionEnd = s + 2; });
  };

  if (!version) return <div className="h-full flex items-center justify-center text-sm text-slate-500" data-testid="code-empty">Code appears here after the first build.</div>;
  const content = all[active] ?? "";
  const lines = content.split("\n").length;

  return (
    <div className="h-full flex" data-testid="code-pane">
      <aside className="w-48 shrink-0 border-r border-slate-200 bg-slate-50/80 flex flex-col">
        <div className="px-3 py-2.5 text-[10px] font-mono uppercase tracking-wider text-slate-500">Files</div>
        <div className="flex-1 overflow-y-auto thin-scroll">
          {paths.map((p) => (
            <div key={p} className={`group flex items-center gap-2 px-3 h-8 text-xs font-mono cursor-pointer ${p === active ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"}`}
              onClick={() => setActive(p)} data-testid={`code-file-${p}`}>
              <FileCode2 size={12} className="shrink-0 text-emerald-600/80" />
              <span className="truncate flex-1">{p}</span>
              {dirty.includes(p) && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
              {!readOnly && paths.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); remove(p); }} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-600" aria-label={`Delete ${p}`} data-testid={`code-delete-${p}`}><Trash2 size={11} /></button>
              )}
            </div>
          ))}
        </div>
        {!readOnly && <NewFile onCreate={(n) => { setDrafts({ ...drafts, [n]: "" }); setActive(n); }} />}
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="h-10 border-b border-slate-200 px-3 flex items-center justify-between text-xs font-mono text-slate-500">
          <span data-testid="code-active-file">{active} <span className="text-slate-400">· {lines} lines</span></span>
          {!readOnly && dirty.length > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={() => setDrafts({})} data-testid="code-discard-btn" className="h-7 px-2.5 rounded-md text-slate-500 hover:text-slate-900 flex items-center gap-1"><Undo2 size={12} /> Discard</button>
              <button onClick={save} disabled={saving} data-testid="code-save-btn" className="h-7 px-3 rounded-md bg-slate-900 text-white font-semibold flex items-center gap-1.5 hover:bg-slate-700">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save {dirty.length} file{dirty.length > 1 ? "s" : ""}
              </button>
            </div>
          )}
        </div>
        <div className="flex-1 flex overflow-auto thin-scroll bg-slate-50">
          <pre className="select-none text-right pr-3 pl-3 py-3 text-[12px] leading-[1.6] font-mono text-slate-300">{Array.from({ length: lines }, (_, i) => i + 1).join("\n")}</pre>
          <textarea
            value={content}
            readOnly={readOnly}
            onChange={(e) => setDrafts({ ...drafts, [active]: e.target.value })}
            onKeyDown={onKey}
            spellCheck={false}
            data-testid="code-editor"
            className="flex-1 min-w-[600px] resize-none bg-transparent py-3 pr-4 text-[12px] leading-[1.6] font-mono text-slate-800 outline-none whitespace-pre overflow-hidden"
            style={{ height: `${lines * 19.2 + 24}px` }}
          />
        </div>
      </div>
    </div>
  );
};

export default CodePane;
